import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import { Diff } from "@/features/FormCard/Diff";

import { InputField, SelectField } from "@/features/FormCard/Fields";

import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { parseIntValue } from "@/features/FormCard/utils";

import { FormActionsUpdate } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";

import { counterparty } from "@/data/counterparty.ts";
import { countries_options } from "@/components/Form/CountryPhone/data";
import { useTranslation } from "react-i18next";

const table_name = "a7_opcvm_issuers";

const FormSchema = z.object({
  denomination_du_sous_jacent: z.string().nullable().optional(),
  lei: z.string().nullable().optional(),
  type_de_contrepartie: z.string().nullable().optional(),
  code_iso_du_pays: z.string().nullable().optional(),
  societe_mere_id: z.preprocess(
    (val) => parseIntValue(val as string),
    z.number().int().nullable().optional(),
  ),
});

export function A7OPCVMIssuersEditCard({ id }: { id: number }) {
  const { t } = useTranslation();
  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    id,
    table_name,
    FormSchema,
    client: "a7",
    disable_translations_request: true,
    fieldsNotApplyTranslation: ["type_de_contrepartie"],
  });

  return (
    <div className="grid place-items-center bg-gray-50 h-full">
      <div className="flex justify-center">
        <Card className="p-4 bg-white">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Émetteur",
                })}{" "}
                {id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-2">
                <div className=" grid grid-cols-1 md:grid-cols-2 gap-2">
                  <InputField
                    name="denomination_du_sous_jacent"
                    label={t(
                      `${table_name}.columns.denomination_du_sous_jacent`,
                      {
                        ns: "tables",
                        defaultValue: "Dénomination",
                      },
                    )}
                    control={form.control}
                  />
                  <InputField
                    name="lei"
                    label={t(`${table_name}.columns.lei`, {
                      ns: "tables",
                      defaultValue: "LEI",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <SelectField
                    name="code_iso_du_pays"
                    label={t(`${table_name}.columns.code_iso_du_pays`, {
                      ns: "tables",
                      defaultValue: "Country",
                    })}
                    options={countries_options.map((c) => ({
                      value: c.value,
                      label: c.label,
                    }))}
                    control={form.control}
                  />
                  <SelectField
                    name="type_de_contrepartie"
                    label={t(`${table_name}.columns.type_de_contrepartie`, {
                      ns: "tables",
                      defaultValue: "Type de contrepartie",
                    })}
                    options={counterparty.map((c) => ({
                      value: c.value,
                      label: c.value,
                    }))}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    allowNegative={false}
                    name="societe_mere_id"
                    label={t(`${table_name}.columns.societe_mere_name`, {
                      ns: "tables",
                      defaultValue: "Parent company name",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsUpdate
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  elementId={id}
                  client="a7"
                  isDirty={form.formState.isDirty}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        {/* Modal to confirm changes */}
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_update")}
          children={
            changedFields
              ? Diff(changedFields, [], undefined, table_name)
              : t("no_changes")
          }
        />

        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onDelete={closeDeleteModal}
        />
      </div>
    </div>
  );
}
